import React, { useEffect, useState } from 'react';
import { Pencil, Trash2, ChevronDown, Plus, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { listBudgetCats, createBudgetCat, deleteBudgetCat, addBudgetItem, deleteBudgetItem, updateBudgetItem, listExpenses, createExpense, deleteExpense, getSavings, addSaving, deleteSaving, setSavingsTarget, formatRupiah, formatRupiahFull, upsertCouple } from '../api';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';

function Donut({ percent, size = 100 }) {
  const r = 42; const c = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" className="-rotate-90">
      <circle cx="50" cy="50" r={r} fill="none" stroke="#EDE3D2" strokeWidth="10" />
      <circle cx="50" cy="50" r={r} fill="none" stroke="#B8642F" strokeWidth="10" strokeDasharray={`${(percent/100)*c} ${c}`} strokeLinecap="round" />
    </svg>
  );
}

export default function Anggaran() {
  const { couple, setCouple } = useAuth();
  const { toast } = useToast();
  const [tab, setTab] = useState('budgeting');
  const [expanded, setExpanded] = useState(null);
  const [cats, setCats] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [savings, setSavings] = useState({ items: [], target: 0, collected: 0 });

  // Dialogs
  const [dlgCat, setDlgCat] = useState(false);
  const [dlgItem, setDlgItem] = useState(null); // cid
  const [dlgExp, setDlgExp] = useState(false);
  const [dlgSav, setDlgSav] = useState(false);
  const [dlgTarget, setDlgTarget] = useState(false);
  const [dlgBudget, setDlgBudget] = useState(false);

  const [catForm, setCatForm] = useState({ name: '', icon: '💰', estimasi: '' });
  const [itemForm, setItemForm] = useState({ name: '', est: '', real: '' });
  const [expForm, setExpForm] = useState({ category: '', item: '', amount: '' });
  const [savForm, setSavForm] = useState({ person: '', amount: '', source: 'Gajian' });
  const [targetVal, setTargetVal] = useState('');
  const [budgetVal, setBudgetVal] = useState('');

  const loadAll = () => {
    listBudgetCats().then(setCats).catch(() => {});
    listExpenses().then(setExpenses).catch(() => {});
    getSavings().then(setSavings).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);

  const totalBudget = Number(couple?.totalBudget) || 0;
  const totalEst = cats.reduce((a,c) => a + (c.estimasi||0), 0);
  const totalReal = cats.reduce((a,c) => a + (c.realisasi||0), 0);
  const percentReal = totalBudget ? Math.round((totalReal/totalBudget)*100) : 0;
  const sisa = totalBudget - totalReal;

  const handleAddCat = async () => {
    if (!catForm.name.trim()) return;
    try { await createBudgetCat({ name: catForm.name, icon: catForm.icon, estimasi: Number(catForm.estimasi)||0 }); setCatForm({ name: '', icon: '💰', estimasi: '' }); setDlgCat(false); loadAll(); toast({ title: 'Kategori ditambahkan' }); }
    catch (e) { toast({ title: 'Gagal', variant: 'destructive' }); }
  };

  const handleDelCat = async (id) => {
    if (!window.confirm('Hapus kategori ini beserta isinya?')) return;
    await deleteBudgetCat(id); loadAll(); toast({ title: 'Kategori dihapus' });
  };

  const handleAddItem = async () => {
    if (!itemForm.name.trim() || !dlgItem) return;
    try { await addBudgetItem(dlgItem, { name: itemForm.name, est: Number(itemForm.est)||0, real: Number(itemForm.real)||0 }); setItemForm({ name: '', est: '', real: '' }); setDlgItem(null); loadAll(); toast({ title: 'Item ditambahkan' }); }
    catch (e) { toast({ title: 'Gagal', variant: 'destructive' }); }
  };

  const handleDelItem = async (id) => { await deleteBudgetItem(id); loadAll(); };

  const handleAddExp = async () => {
    if (!expForm.category || !expForm.item || !expForm.amount) { toast({ title: 'Lengkapi form', variant: 'destructive' }); return; }
    try { await createExpense({ category: expForm.category, item: expForm.item, amount: Number(expForm.amount) }); setExpForm({ category: '', item: '', amount: '' }); setDlgExp(false); loadAll(); toast({ title: 'Pengeluaran dicatat' }); }
    catch (e) { toast({ title: 'Gagal', variant: 'destructive' }); }
  };

  const handleDelExp = async (id) => { await deleteExpense(id); loadAll(); };

  const handleAddSav = async () => {
    if (!savForm.person || !savForm.amount) { toast({ title: 'Lengkapi form', variant: 'destructive' }); return; }
    try { await addSaving({ person: savForm.person, amount: Number(savForm.amount), source: savForm.source }); setSavForm({ person: '', amount: '', source: 'Gajian' }); setDlgSav(false); loadAll(); toast({ title: 'Tabungan dicatat' }); }
    catch (e) { toast({ title: 'Gagal', variant: 'destructive' }); }
  };

  const handleDelSav = async (id) => { await deleteSaving(id); loadAll(); };

  const handleSetTarget = async () => {
    if (!targetVal) return;
    await setSavingsTarget(Number(targetVal)); setDlgTarget(false); loadAll(); toast({ title: 'Target diperbarui' });
  };

  const handleUpdateBudget = async () => {
    if (!budgetVal || !couple) return;
    const saved = await upsertCouple({ ...couple, totalBudget: Number(budgetVal) });
    setCouple(saved); setDlgBudget(false); toast({ title: 'Total budget diperbarui' });
  };

  return (
    <div className="px-5 pt-8 lg:pt-10">
      <h1 className="font-serif text-4xl text-sage-700 mb-5">Anggaran</h1>

      <div className="bg-white rounded-2xl p-5 border border-cream-200 mb-5 flex items-center gap-5">
        <div className="relative" style={{width: 100, height: 100}}>
          <Donut percent={percentReal} />
          <div className="absolute inset-0 flex items-center justify-center font-serif text-terracotta-500 text-xl">{percentReal}%</div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-serif text-3xl text-sage-700">{formatRupiah(totalReal)}</div>
          <div className="text-xs text-sage-500">realisasi dari {formatRupiahFull(totalBudget)}</div>
          <div className="flex flex-wrap gap-3 mt-2 text-xs">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-terracotta-500" /> Est. {formatRupiah(totalEst)}</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-sage-600" /> Sisa {formatRupiah(sisa)}</span>
          </div>
        </div>
      </div>

      <div className="bg-cream-200 rounded-2xl p-1 grid grid-cols-3 mb-5">
        {[{id:'budgeting',label:'Budgeting'},{id:'pengeluaran',label:'Pengeluaran'},{id:'dana',label:'Dana Nikah'}].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`py-2.5 rounded-xl text-sm font-medium transition-colors ${tab === t.id ? 'bg-white text-sage-700 shadow-sm' : 'text-sage-500'}`}>{t.label}</button>
        ))}
      </div>

      {tab === 'budgeting' && (
        <div className="slide-up">
          <div className="grid grid-cols-3 text-center mb-6">
            <div>
              <div className="font-semibold text-sage-700">{formatRupiah(totalBudget)}</div>
              <div className="text-xs text-sage-500">Total Budget · <button onClick={() => { setBudgetVal(totalBudget); setDlgBudget(true); }} className="underline">ubah</button></div>
            </div>
            <div>
              <div className="font-semibold text-terracotta-500">{formatRupiah(totalEst)}</div>
              <div className="text-xs text-sage-500">Estimasi</div>
            </div>
            <div>
              <div className="font-semibold text-sage-600">{formatRupiah(totalBudget - totalEst)}</div>
              <div className="text-xs text-sage-500">Sisa Est.</div>
            </div>
          </div>

          <div className="space-y-3">
            {cats.map(c => (
              <div key={c.id} className="bg-white rounded-2xl border border-cream-200 overflow-hidden">
                <div className="p-4 flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-cream-100 flex items-center justify-center text-xl">{c.icon}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sage-700">{c.name}</div>
                    <div className="text-xs text-sage-500 flex flex-wrap gap-x-3 mt-0.5">
                      <span>Est. {formatRupiah(c.estimasi)}</span>
                      {c.realisasi > 0 && <span className="text-terracotta-500">Realisasi {formatRupiah(c.realisasi)}</span>}
                      <span>· {c.items?.length || 0} item</span>
                    </div>
                  </div>
                  <button onClick={() => handleDelCat(c.id)} className="w-8 h-8 rounded-full bg-red-50 flex items-center justify-center"><Trash2 className="w-3.5 h-3.5 text-red-500" /></button>
                  <button onClick={() => setExpanded(expanded === c.id ? null : c.id)} className="w-8 h-8 rounded-full bg-cream-100 flex items-center justify-center"><ChevronDown className={`w-4 h-4 text-sage-500 transition-transform ${expanded === c.id ? 'rotate-180' : ''}`} /></button>
                </div>
                {expanded === c.id && (
                  <div className="border-t border-cream-100 divide-y divide-cream-100">
                    {(c.items || []).map(item => (
                      <div key={item.id} className="px-5 py-3 flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full bg-sage-300" />
                        <div className="flex-1 min-w-0 text-sm text-sage-700">{item.name}</div>
                        <div className="text-right">
                          <div className="text-sm font-medium text-sage-700">{formatRupiah(item.est)}</div>
                          {item.real > 0 && <div className="text-xs text-terracotta-500">Realisasi {formatRupiah(item.real)}</div>}
                        </div>
                        <button onClick={() => handleDelItem(item.id)} className="w-7 h-7 rounded-full bg-red-50 flex items-center justify-center"><Trash2 className="w-3 h-3 text-red-500" /></button>
                      </div>
                    ))}
                    <button onClick={() => { setItemForm({ name: '', est: '', real: '' }); setDlgItem(c.id); }} className="w-full py-3 text-terracotta-500 text-sm font-medium hover:bg-cream-50">+ Tambah item</button>
                  </div>
                )}
              </div>
            ))}
          </div>
          <button onClick={() => setDlgCat(true)} className="w-full py-4 mt-4 text-terracotta-500 font-medium flex items-center justify-center gap-2"><Plus className="w-4 h-4" /> Tambah Kategori</button>
        </div>
      )}

      {tab === 'pengeluaran' && (
        <div className="slide-up space-y-3">
          <div className="flex justify-between items-center">
            <div className="text-sm text-sage-500">Total: <span className="font-semibold text-sage-700">{formatRupiahFull(expenses.reduce((a,e)=>a+e.amount,0))}</span></div>
            <Button onClick={() => setDlgExp(true)} className="bg-terracotta-500 hover:bg-terracotta-600 text-white rounded-full h-9 text-xs"><Plus className="w-3.5 h-3.5 mr-1" /> Catat</Button>
          </div>
          {expenses.map(e => (
            <div key={e.id} className="bg-white rounded-2xl p-4 border border-cream-200 flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-terracotta-50 flex items-center justify-center text-xl">💸</div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sage-700 text-sm">{e.item}</div>
                <div className="text-xs text-sage-500">{e.category} · {new Date(e.date).toLocaleDateString('id-ID', {day:'numeric', month:'short', year:'numeric'})}</div>
              </div>
              <div className="font-semibold text-terracotta-500">{formatRupiah(e.amount)}</div>
              <button onClick={() => handleDelExp(e.id)} className="w-8 h-8 rounded-full bg-red-50 flex items-center justify-center"><Trash2 className="w-3.5 h-3.5 text-red-500" /></button>
            </div>
          ))}
          {expenses.length === 0 && <div className="text-center py-12 text-sage-400 text-sm">Belum ada pengeluaran</div>}
        </div>
      )}

      {tab === 'dana' && (
        <div className="slide-up">
          <div className="bg-gradient-to-br from-terracotta-500 to-terracotta-600 rounded-2xl p-6 text-white mb-5">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-xs text-cream-100">Dana terkumpul</div>
                <div className="font-serif text-4xl mt-1">Rp{(savings.collected||0).toLocaleString('id-ID')}</div>
              </div>
              <button onClick={() => { setTargetVal(savings.target); setDlgTarget(true); }} className="bg-white/20 hover:bg-white/30 rounded-full px-4 py-1.5 text-xs">Atur target</button>
            </div>
            <div className="h-2 bg-white/25 rounded-full overflow-hidden mb-2">
              <div className="h-full bg-white" style={{width: `${savings.target ? Math.min(100, (savings.collected/savings.target)*100) : 0}%`}} />
            </div>
            <div className="flex justify-between text-xs text-cream-100">
              <span>{savings.target ? Math.round((savings.collected/savings.target)*100) : 0}% terkumpul</span>
              <span>Target {formatRupiah(savings.target)}</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-5">
            <div className="bg-white rounded-2xl p-4 border border-cream-200">
              <div className="text-xs text-sage-500">Masih dibutuhkan</div>
              <div className="font-serif text-2xl text-sage-700">{formatRupiah(Math.max(0, (savings.target||0) - (savings.collected||0)))}</div>
            </div>
            <div className="bg-white rounded-2xl p-4 border border-cream-200">
              <div className="text-xs text-sage-500">Saran per bulan</div>
              <div className="font-serif text-2xl text-sage-700">{formatRupiah(Math.max(0, Math.round(((savings.target||0)-(savings.collected||0))/12)))}</div>
            </div>
          </div>

          <div className="flex items-center justify-between mb-3">
            <h3 className="font-serif text-xl text-sage-700">Riwayat tabungan</h3>
            <Button onClick={() => setDlgSav(true)} className="bg-terracotta-500 hover:bg-terracotta-600 text-white rounded-full h-9 text-xs"><Plus className="w-3.5 h-3.5 mr-1" /> Catat</Button>
          </div>
          <div className="bg-white rounded-2xl border border-cream-200 divide-y divide-cream-100">
            {savings.items.map(s => (
              <div key={s.id} className="p-4 flex items-center">
                <div className="flex-1">
                  <div className="font-medium text-sage-700">{s.person}</div>
                  <div className="text-xs text-sage-500">{new Date(s.date).toLocaleDateString('id-ID', {day:'numeric', month:'short', year:'numeric'})} · {s.source}</div>
                </div>
                <div className="text-sage-600 font-semibold">+{formatRupiah(s.amount)}</div>
                <button onClick={() => handleDelSav(s.id)} className="ml-3 text-red-400 text-lg leading-none">×</button>
              </div>
            ))}
            {savings.items.length === 0 && <div className="text-center py-8 text-sage-400 text-sm">Belum ada tabungan</div>}
          </div>
        </div>
      )}

      {/* Dialogs */}
      <Dialog open={dlgCat} onOpenChange={setDlgCat}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-serif text-sage-700">Tambah Kategori Budget</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nama</Label><Input value={catForm.name} onChange={(e)=>setCatForm({...catForm, name:e.target.value})} className="mt-1 bg-cream-50" /></div>
            <div><Label>Icon (emoji)</Label><Input value={catForm.icon} onChange={(e)=>setCatForm({...catForm, icon:e.target.value})} className="mt-1 bg-cream-50" /></div>
            <div><Label>Estimasi (Rp)</Label><Input type="number" value={catForm.estimasi} onChange={(e)=>setCatForm({...catForm, estimasi:e.target.value})} className="mt-1 bg-cream-50" /></div>
          </div>
          <DialogFooter><Button onClick={handleAddCat} className="bg-terracotta-500 hover:bg-terracotta-600 text-white">Tambah</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!dlgItem} onOpenChange={(o) => !o && setDlgItem(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-serif text-sage-700">Tambah Item</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nama item</Label><Input value={itemForm.name} onChange={(e)=>setItemForm({...itemForm, name:e.target.value})} className="mt-1 bg-cream-50" /></div>
            <div><Label>Estimasi (Rp)</Label><Input type="number" value={itemForm.est} onChange={(e)=>setItemForm({...itemForm, est:e.target.value})} className="mt-1 bg-cream-50" /></div>
            <div><Label>Realisasi (Rp, opsional)</Label><Input type="number" value={itemForm.real} onChange={(e)=>setItemForm({...itemForm, real:e.target.value})} className="mt-1 bg-cream-50" /></div>
          </div>
          <DialogFooter><Button onClick={handleAddItem} className="bg-terracotta-500 hover:bg-terracotta-600 text-white">Tambah</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dlgExp} onOpenChange={setDlgExp}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-serif text-sage-700">Catat Pengeluaran</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Kategori</Label><Input value={expForm.category} onChange={(e)=>setExpForm({...expForm, category:e.target.value})} placeholder="mis. Katering" className="mt-1 bg-cream-50" /></div>
            <div><Label>Item</Label><Input value={expForm.item} onChange={(e)=>setExpForm({...expForm, item:e.target.value})} placeholder="mis. DP Catering" className="mt-1 bg-cream-50" /></div>
            <div><Label>Jumlah (Rp)</Label><Input type="number" value={expForm.amount} onChange={(e)=>setExpForm({...expForm, amount:e.target.value})} className="mt-1 bg-cream-50" /></div>
          </div>
          <DialogFooter><Button onClick={handleAddExp} className="bg-terracotta-500 hover:bg-terracotta-600 text-white">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dlgSav} onOpenChange={setDlgSav}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-serif text-sage-700">Catat Tabungan</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nama</Label><Input value={savForm.person} onChange={(e)=>setSavForm({...savForm, person:e.target.value})} placeholder="mis. Dimas" className="mt-1 bg-cream-50" /></div>
            <div><Label>Jumlah (Rp)</Label><Input type="number" value={savForm.amount} onChange={(e)=>setSavForm({...savForm, amount:e.target.value})} className="mt-1 bg-cream-50" /></div>
            <div><Label>Sumber</Label><Input value={savForm.source} onChange={(e)=>setSavForm({...savForm, source:e.target.value})} placeholder="Gajian, Bonus, THR..." className="mt-1 bg-cream-50" /></div>
          </div>
          <DialogFooter><Button onClick={handleAddSav} className="bg-terracotta-500 hover:bg-terracotta-600 text-white">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dlgTarget} onOpenChange={setDlgTarget}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-serif text-sage-700">Atur Target Dana</DialogTitle></DialogHeader>
          <div><Label>Target (Rp)</Label><Input type="number" value={targetVal} onChange={(e)=>setTargetVal(e.target.value)} className="mt-1 bg-cream-50" /></div>
          <DialogFooter><Button onClick={handleSetTarget} className="bg-terracotta-500 hover:bg-terracotta-600 text-white">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dlgBudget} onOpenChange={setDlgBudget}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-serif text-sage-700">Ubah Total Budget</DialogTitle></DialogHeader>
          <div><Label>Total Budget (Rp)</Label><Input type="number" value={budgetVal} onChange={(e)=>setBudgetVal(e.target.value)} className="mt-1 bg-cream-50" /></div>
          <DialogFooter><Button onClick={handleUpdateBudget} className="bg-terracotta-500 hover:bg-terracotta-600 text-white">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
